import { apiv2Get } from '@/lib/apiv2';
import { eventPost } from '@/lib/events';
import { currentUpdaterRole } from '@/config/program';

export interface ContentCatalogItem {
  catalog_id: string;
  slug?: string;
  title?: string;
  track?: string;
  [key: string]: unknown;
}

export interface CustomContent {
  catalog_id?: string;
  title?: string;
  body?: string;
  [key: string]: unknown;
}

export async function getContentCatalog(
  filters: Record<string, unknown> = {},
): Promise<ContentCatalogItem[]> {
  const rows = await apiv2Get('content-catalog', { active: 'true', ...filters });
  return rows || [];
}

export async function scheduleContentTask(
  withingsUserId: string | number,
  catalogId: string,
  scheduledDate: string,
  priority = 'low',
): Promise<unknown> {
  return eventPost('content_task_scheduled', {
    id_dev_user: String(withingsUserId),
    catalog_id: catalogId,
    scheduled_date: scheduledDate,
    priority,
  });
}

// catalog_id is required by the Tier 4 contract — custom content uses a 'custom' sentinel WF28 recognizes.
export async function scheduleCustomContentTask(
  withingsUserId: string | number,
  customContent: CustomContent,
  scheduledDate: string,
  priority = 'low',
): Promise<unknown> {
  return eventPost('content_task_scheduled', {
    id_dev_user: String(withingsUserId),
    custom_content: customContent,
    scheduled_date: scheduledDate,
    priority,
    catalog_id: customContent?.catalog_id || 'custom',
  });
}

// task_updated needs id_dev_user: without it apiv2's patient-program check 214s even a coordinator.
export async function cancelContentTask(taskId: string | number, withingsUserId: string | number): Promise<unknown> {
  return eventPost('task_updated', {
    task_id: taskId,
    id_dev_user: withingsUserId,
    updated_by_role: currentUpdaterRole(),
    changes: { status: 'cancelled' },
  });
}

export async function reactivateContentTask(taskId: string | number, withingsUserId: string | number): Promise<unknown> {
  return eventPost('task_updated', {
    task_id: taskId,
    id_dev_user: withingsUserId,
    updated_by_role: currentUpdaterRole(),
    changes: { status: 'scheduled' },
  });
}

// newDate is YYYY-MM-DD (local). due_at = newDate + 3d @ 17:00.
export async function rescheduleContentTask(
  taskId: string | number,
  newDate: string,
  withingsUserId: string | number,
): Promise<unknown> {
  const available = `${newDate}T09:00:00Z`;
  // new Date('YYYY-MM-DD') parses as UTC midnight — a day early west of UTC.
  const [y = NaN, m = NaN, d = NaN] = newDate.split('-').map(Number);
  const due = new Date(y, m - 1, d + 3, 17, 0, 0, 0);
  return eventPost('task_updated', {
    task_id: taskId,
    id_dev_user: withingsUserId,
    updated_by_role: currentUpdaterRole(),
    changes: { available_at: available, due_at: due.toISOString() },
  });
}
